import dayjs, { Dayjs } from 'dayjs';
import type {
  FedExPickup,
  FedExPickupPrefill,
  PickupAddress,
  PickupCarrierType,
  PickupContact,
  PickupWeightUnit,
} from '@/apis/fedexPickup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { weightKgToLb, weightLbToKg } from '../shellShipmentHelpers';

const DATE_FORMAT = 'YYYY-MM-DD';
const GROUND_MAX_DAYS_OUT = 14;

const isWeekend = (d: Dayjs) => d.day() === 0 || d.day() === 6;

/** Parses the origin's calendar day (not the browser's) as a local date. */
export const parseOriginToday = (originToday?: string): Dayjs =>
  originToday && dayjs(originToday).isValid()
    ? dayjs(originToday)
    : dayjs().startOf('day');

export const nextBusinessDay = (from: Dayjs): Dayjs => {
  let next = from.add(1, 'day');
  while (isWeekend(next)) next = next.add(1, 'day');
  return next;
};

export const getPickupDateBounds = (
  carrierType: PickupCarrierType,
  originToday?: string
): { min: string; max: string } => {
  const today = parseOriginToday(originToday);
  return carrierType === 'EXPRESS'
    ? {
        min: today.format(DATE_FORMAT),
        max: nextBusinessDay(today).format(DATE_FORMAT),
      }
    : {
        min: today.add(1, 'day').format(DATE_FORMAT),
        max: today.add(GROUND_MAX_DAYS_OUT, 'day').format(DATE_FORMAT),
      };
};

export const clampDate = (
  date: string,
  bounds: { min: string; max: string }
): string =>
  date < bounds.min ? bounds.min : date > bounds.max ? bounds.max : date;

export const getDefaultPickupDate = (
  carrierType: PickupCarrierType,
  originToday?: string
): string => {
  const { min } = getPickupDateBounds(carrierType, originToday);
  const minDay = dayjs(min);
  return (isWeekend(minDay) ? nextBusinessDay(minDay) : minDay).format(
    DATE_FORMAT
  );
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** kg↔lb via the shared shipment helpers; 0 when the weight isn't a positive number. */
export const convertWeight = (
  weight: number,
  from: PickupWeightUnit,
  to: PickupWeightUnit
): number => {
  if (from === to) return weight;
  const converted = from === 'KG' ? weightKgToLb(weight) : weightLbToKg(weight);
  return converted ?? 0;
};

export const roundWeight = round2;

// Same NANP rule as the backend (area code and exchange can't start with 0 or 1),
// so the form and the API agree on what a usable US number is.
const NANP_NATIONAL = /^[2-9]\d{2}[2-9]\d{6}$/;

/** "+1 516-555-5555 x22" for display; falls back to the raw value when it isn't a parseable US number. */
export const formatUsPhone = (phone: string): string => {
  const parsed = parsePhoneNumberFromString(phone, 'US');
  if (parsed?.countryCallingCode !== '1' || parsed.nationalNumber.length !== 10)
    return phone;
  const n = parsed.nationalNumber;
  const ext = parsed.ext ? ` x${parsed.ext}` : '';
  return `+1 ${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}${ext}`;
};

/** US number in any common format (+1, extension allowed), parsed with libphonenumber-js. */
export const isValidUsPhone = (phone: string): boolean => {
  const parsed = parsePhoneNumberFromString(phone, 'US');
  return (
    !!parsed &&
    parsed.countryCallingCode === '1' &&
    NANP_NATIONAL.test(parsed.nationalNumber)
  );
};

export const MAX_PACKAGE_COUNT = 999;

export const to12Hour = (time: string): string =>
  dayjs(`2000-01-01T${time}`).format('h:mm A');

/** e.g. "8/12/24 10:30 AM CDT" for a scheduled pickup's ready time in the origin timezone. */
export const formatPickupStart = (
  pickup: Pick<FedExPickup, 'pickupDate' | 'readyTime'>,
  timezone: string
): string => {
  const start = dayjs(`${pickup.pickupDate}T${pickup.readyTime}`);
  const abbreviation = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(start.toDate())
    .find((part) => part.type === 'timeZoneName')?.value;
  return [start.format('M/D/YY h:mm A'), abbreviation]
    .filter(Boolean)
    .join(' ');
};

export const isAddressComplete = (address: PickupAddress): boolean =>
  !!(
    address.street.trim() &&
    address.city.trim() &&
    address.state.length === 2 &&
    address.postalCode.trim()
  );

/** Whether the prefill allows scheduling. The panel shows the address read-only and submits it as US, so non-US can't be fixed there. */
export const canSchedulePickupFromPrefill = (
  prefill?: FedExPickupPrefill | null
): boolean =>
  !!prefill &&
  prefill.activePickup?.status !== 'SCHEDULED' &&
  (prefill.eligible || prefill.manualAllowed) &&
  prefill.address.country.trim().toUpperCase() === 'US' &&
  isAddressComplete(prefill.address);

interface PickupFormFields {
  address: PickupAddress;
  contact: PickupContact;
  readyTime: string;
  closeTime: string;
  packageCount: number | null;
  weight: number | null;
}

/** Field-level completeness; availability and residential rules are checked separately. */
export const isPickupFormComplete = ({
  address,
  contact,
  readyTime,
  closeTime,
  packageCount,
  weight,
}: PickupFormFields): boolean =>
  isAddressComplete(address) &&
  !!(contact.name.trim() || contact.company.trim()) &&
  isValidUsPhone(contact.phone) &&
  !!readyTime &&
  !!closeTime &&
  readyTime < closeTime &&
  !!packageCount &&
  packageCount >= 1 &&
  packageCount <= MAX_PACKAGE_COUNT &&
  (weight == null || weight > 0);
